import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

const Footer: React.FC = () => {
  const { t, i18n } = useTranslation();
  const currentLang = i18n.language;
  
  const changeLanguage = (lang: string): void => {
    i18n.changeLanguage(lang);
  };

  // Fonction pour gérer les modales légales
  const handleLegalClick = (type: string) => (e: React.MouseEvent) => {
    e.preventDefault();
    // Afficher une alerte simple pour le moment, à remplacer par une modale dans une version future
    alert(t(`footer.legal.${type}_content`) || `Contenu ${type} à venir`);
  };

  return (
    <footer className="bg-mypa-blue text-white pt-12 pb-6">
      <div className="container mx-auto px-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8">
          {/* Logo and description */}
          <div className="col-span-1 md:col-span-1">
            <Link to="/" className="flex items-center mb-4">
              <img 
                src="/src/assets/images/logo.jpg" 
                alt="mypa logo" 
                className="h-10 w-auto bg-white rounded-full p-1"
              />
              <span className="ml-2 text-xl font-bold">mypa</span>
            </Link>
            <p className="text-gray-300 mb-4">
              {t("about.mission.description") || ""}
            </p>
            <div className="flex space-x-4">
              <a href="https://www.linkedin.com/company/mypa" target="_blank" rel="noopener noreferrer" className="text-white hover:text-gray-300">
                <span className="sr-only">LinkedIn</span>
                <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z"></path>
                </svg>
              </a>
              <a href="https://twitter.com/mypa_earth" target="_blank" rel="noopener noreferrer" className="text-white hover:text-gray-300">
                <span className="sr-only">Twitter</span>
                <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M8.29 20.251c7.547 0 11.675-6.253 11.675-11.675 0-.178 0-.355-.012-.53A8.348 8.348 0 0022 5.92a8.19 8.19 0 01-2.357.646 4.118 4.118 0 001.804-2.27 8.224 8.224 0 01-2.605.996 4.107 4.107 0 00-6.993 3.743 11.65 11.65 0 01-8.457-4.287 4.106 4.106 0 001.27 5.477A4.072 4.072 0 012.8 9.713v.052a4.105 4.105 0 003.292 4.022 4.095 4.095 0 01-1.853.07 4.108 4.108 0 003.834 2.85A8.233 8.233 0 012 18.407a11.616 11.616 0 006.29 1.84"></path>
                </svg>
              </a>
            </div>
          </div>
          
          {/* Quick links */}
          <div>
            <h3 className="text-lg font-bold mb-4">{t("nav.home") || "Accueil"}</h3>
            <ul className="space-y-2">
              <li>
                <Link to="/approach" className="text-gray-300 hover:text-white transition-colors">
                  {t("nav.approach") || "Notre approche"}
                </Link>
              </li>
              <li>
                <Link to="/offers" className="text-gray-300 hover:text-white transition-colors">
                  {t("nav.offers") || "Nos offres"}
                </Link>
              </li>
              <li>
                <Link to="/segments" className="text-gray-300 hover:text-white transition-colors">
                  {t("nav.segments") || "Segments clients"}
                </Link>
              </li>
              <li>
                <Link to="/calculator" className="text-gray-300 hover:text-white transition-colors">
                  {t("nav.calculator") || "Calculateur carbone"}
                </Link>
              </li>
              <li>
                <Link to="/blog" className="text-gray-300 hover:text-white transition-colors">
                  {t("nav.blog") || "Blog"}
                </Link>
              </li>
            </ul>
          </div>
          
          {/* Legal */}
          <div>
            <h3 className="text-lg font-bold mb-4">{t("footer.company") || "Entreprise"}</h3>
            <ul className="space-y-2">
              <li>
                <a href="#" onClick={handleLegalClick('legal')} className="text-gray-300 hover:text-white transition-colors">
                  {t("footer.legal") || "Mentions légales"}
                </a>
              </li>
              <li>
                <a href="#" onClick={handleLegalClick('privacy')} className="text-gray-300 hover:text-white transition-colors">
                  {t("footer.privacy") || "Politique de confidentialité"}
                </a>
              </li>
              <li>
                <a href="#" onClick={handleLegalClick('terms')} className="text-gray-300 hover:text-white transition-colors">
                  {t("footer.terms") || "Conditions d'utilisation"}
                </a>
              </li>
              <li>
                <a href="#" onClick={handleLegalClick('cookies')} className="text-gray-300 hover:text-white transition-colors">
                  {t("footer.cookies") || "Gestion des cookies"}
                </a>
              </li>
            </ul>
          </div>
          
          {/* Newsletter */}
          <div>
            <h3 className="text-lg font-bold mb-4">{t("footer.newsletter.title") || "Newsletter"}</h3>
            <p className="text-gray-300 mb-4">{t("footer.newsletter.description") || "Restez informé de nos actualités"}</p>
            <form className="flex">
              <input
                type="email"
                placeholder={t("footer.newsletter.placeholder") || "Votre email"}
                className="flex-grow px-4 py-2 rounded-l-lg focus:outline-none text-gray-900"
              />
              <button
                type="submit"
                className="bg-mypa-green text-white px-4 py-2 rounded-r-lg hover:bg-opacity-90 transition-colors"
              >
                {t("footer.newsletter.button") || "S'abonner"}
              </button>
            </form>
          </div>
        </div>
        
        {/* Bottom bar */}
        <div className="border-t border-gray-700 pt-6 flex flex-col md:flex-row justify-between items-center">
          <p className="text-gray-300 mb-4 md:mb-0">{t("footer.copyright") || "© 2025 mypa.earth. Tous droits réservés."}</p>
          
          {/* Language switcher */}
          <div className="flex items-center">
            <span className="text-gray-300 mr-4">{currentLang === "fr" ? "Langue:" : "Language:"}</span>
            <button
              onClick={() => changeLanguage("fr")}
              className={`mr-2 px-2 py-1 rounded ${
                currentLang === "fr" ? "bg-mypa-green text-white" : "text-gray-300 hover:text-white"
              }`}
            >
              FR
            </button>
            <button
              onClick={() => changeLanguage("en")}
              className={`px-2 py-1 rounded ${
                currentLang === "en" ? "bg-mypa-green text-white" : "text-gray-300 hover:text-white"
              }`}
            >
              EN
            </button>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
